import * as React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useFeatures } from '@xing-com/hub';
import { mediaTopBarHidden, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { Me } from '@xing-com/platform-layout-me';
import { space3XL, spaceM, spaceS, spaceXL } from '@xing-com/tokens';

import { MeMenuItem } from './me-menu-item';
import { MeMenuItemVomp } from './me-menu-item-vomp';

const TopItemsSection = styled.div`
  margin-top: ${spaceXL};

  @media ${mediaTopBarVisible} {
    margin-top: ${space3XL};
  }
`;

const FeatureBasedItemsSection = styled.div`
  margin-top: ${spaceS};
  margin-bottom: ${spaceS};

  @media ${mediaTopBarVisible} {
    margin-top: ${spaceM};
    margin-bottom: ${spaceM};
  }
`;

const BigScreenMeMenuItemContainer = styled.div`
  @media ${mediaTopBarVisible} {
    display: none;
  }
`;

const SmallScreenMeMenuItemContainer = styled.div`
  @media ${mediaTopBarHidden} {
    display: none;
  }
`;

export const MeMenuDropdownList: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const features = useFeatures();

  const { isPremium, hasProJobsMembership, isJobsPoster } = features.data || {};

  return (
    <>
      <Me
        href="/profile"
        isOnMeMenu
        tracking={{
          nwt_nav: 'profile_icon',
          sc_o: 'navigation_profile_icon',
        }}
      />

      <TopItemsSection>
        <MeMenuItemVomp
          href="/profile"
          dataTestId="me-menu-item-profile"
          title={formatMessage({ id: 'MEMENU_PROFILE_LABEL' })}
          tracking={{
            nwt_nav: 'profile',
            sc_o: 'navigation_profile',
          }}
        />

        <MeMenuItem
          href="/jobseeker-criteria"
          dataTestId="me-menu-item-jobseeker-criteria"
          title={formatMessage({ id: 'MEMENU_JOB_PREFERENCES_LABEL' })}
          tracking={{
            nwt_nav: 'job_preferences',
            sc_o: 'navigation_job_preferences',
          }}
        />

        <MeMenuItem
          href="/news/selections/following"
          dataTestId="me-menu-item-following"
          title={formatMessage({ id: 'MEMENU_FOLLOWING_LABEL' })}
          tracking={{
            nwt_nav: 'following',
            sc_o: 'navigation_following',
          }}
        />
        <MeMenuItem
          href="/discover/your-posts"
          dataTestId="me-menu-item-your-posts"
          title={formatMessage({ id: 'MEMENU_MY_POSTS_LABEL' })}
          tracking={{
            nwt_nav: 'your_posts',
            sc_o: 'navigation_your_posts',
          }}
        />
      </TopItemsSection>

      <FeatureBasedItemsSection>
        {isPremium ? (
          <MeMenuItem
            title={formatMessage({ id: 'MEMENU_PREMIUM_LABEL' })}
            href="/premium"
            dataTestId="me-menu-item-premium"
            color="premium"
            tracking={{
              nwt_nav: 'premium',
              sc_o: 'navigation_premium',
            }}
          />
        ) : (
          <MeMenuItem
            activeItemValues={['premium']}
            title={formatMessage({ id: 'MEMENU_PREMIUM_FOR_BASIC_USER_LABEL' })}
            href="/purchase/premium?reagent=uplt_1014"
            dataTestId="me-menu-item-premium-upsell"
            color="premium"
            tracking={{
              nwt_nav: 'premium',
              sc_o: 'navigation_premium',
            }}
          />
        )}

        {hasProJobsMembership ? (
          <MeMenuItem
            title={formatMessage({ id: 'MEMENU_PROJOBS_LABEL' })}
            href="/projobs"
            dataTestId="me-menu-item-projobs"
            color="proJobs"
            tracking={{
              nwt_nav: 'projobs',
              sc_o: 'navigation_projobs',
            }}
          />
        ) : (
          <MeMenuItem
            title={formatMessage({ id: 'MEMENU_PROJOBS_FOR_BASIC_USER_LABEL' })}
            color="proJobs"
            href="/purchase/projobs?reagent=uplt_1015"
            dataTestId="me-menu-item-projobs-upsell"
            tracking={{
              nwt_nav: 'projobs',
              sc_o: 'navigation_projobs',
            }}
          />
        )}
      </FeatureBasedItemsSection>

      <SmallScreenMeMenuItemContainer>
        {isJobsPoster ? (
          <MeMenuItem
            title={formatMessage({ id: 'MEMENU_JOB_ADS_LABEL' })}
            href="/jobmanager/online"
            dataTestId="me-menu-item-job-ads"
            tracking={{
              nwt_nav: 'manage_jobs_ads',
              sc_o: 'navigation_manage_jobs_ads',
            }}
          />
        ) : null}
      </SmallScreenMeMenuItemContainer>

      <BigScreenMeMenuItemContainer>
        <MeMenuItem
          href="/settings/purchases"
          dataTestId="me-menu-item-purchases"
          title={formatMessage({
            id: 'VERTICAL_NAVIGATION_HEADER_PERSONAL_MEMBERSHIPS_INVOICES',
          })}
          tracking={{
            nwt_nav: 'memberships_invoices',
            sc_o: 'navigation_memberships_invoices',
          }}
        />
      </BigScreenMeMenuItemContainer>
      <MeMenuItem
        href="/settings"
        dataTestId="me-menu-item-settings"
        title={formatMessage({ id: 'MEMENU_SETTINGS_LABEL' })}
        tracking={{
          nwt_nav: 'settings',
          sc_o: 'navigation_settings',
        }}
      />
      <MeMenuItem
        href={`https://faq.xing.com/${locale}`}
        title={formatMessage({ id: 'MEMENU_HELP_LABEL' })}
        dataTestId="me-menu-item-help"
        tracking={{
          nwt_nav: 'help',
          sc_o: 'navigation_help',
        }}
      />
      <BigScreenMeMenuItemContainer>
        <MeMenuItem
          href="/login/logout"
          dataTestId="me-menu-item-logout"
          title={formatMessage({
            id: 'VERTICAL_NAVIGATION_HEADER_PERSONAL_LOGOUT',
          })}
          tracking={{
            nwt_nav: 'logout',
            sc_o: 'navigation_logout',
          }}
        />
      </BigScreenMeMenuItemContainer>
    </>
  );
};
